import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { AREA_GUIDES, CITIES } from "../data/areaGuides";

const AreaGuides = () => {
  const [city, setCity] = useState("Bath");
  const guides = AREA_GUIDES.filter((a) => (a.city || "Bath") === city);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Bath Area Guides — Anadear Estates",
    itemListElement: AREA_GUIDES.map((a, i) => ({
      "@type": "ListItem", position: i + 1, name: `${a.name}, Bath ${a.postcode}`,
      url: `https://anadearestates.com/area-guides/${a.slug}`,
    })),
  };
  return (
    <div className="min-h-[70vh] bg-[#faf7f2]" data-testid="area-guides-page">
      <Seo
        title="Bath Area Guides — Widcombe, Lansdown, Bathwick & more"
        description="Local knowledge you can trust. Explore Anadear Estates' hyper-local Bath neighbourhood guides — property prices, schools, transport and lifestyle for Widcombe, Lansdown, Bathwick, Larkhall, Combe Down & Weston."
        path="/area-guides"
        jsonLd={jsonLd}
      />
      <div className="bg-[#1c1c1e] py-16">
        <div className="max-w-[1180px] mx-auto px-5 md:px-8">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#d9b98a] mb-2 flex items-center gap-2"><MapPin size={15} /> Local Knowledge</p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-300 leading-tight">Bath &amp; Bristol Area Guides</h1>
          <p className="text-white/55 text-base mt-4 max-w-2xl">Choosing where to live is as important as the home itself. Our area experts break down the character, prices, schools and lifestyle of each Bath &amp; Bristol neighbourhood — so you can buy, sell or invest with confidence.</p>
          <div className="inline-flex rounded-full bg-white/10 border border-white/15 p-1 mt-7" data-testid="city-toggle">
            {CITIES.map((c) => (
              <button key={c} onClick={() => setCity(c)} data-testid={`city-${c.toLowerCase()}`} className={`px-6 py-2 rounded-full text-[14px] font-500 transition-colors ${city === c ? "bg-[#b8935f] text-white" : "text-white/70 hover:text-white"}`}>{c}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1180px] mx-auto px-5 md:px-8 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {guides.map((a) => (
            <Link
              key={a.slug}
              to={`/area-guides/${a.slug}`}
              data-testid={`area-card-${a.slug}`}
              className="group bg-white rounded-2xl overflow-hidden border border-[#f0ece5] hover:shadow-[0_18px_50px_rgba(0,0,0,0.10)] hover:border-[#b8935f]/40 transition-all duration-300"
            >
              <div className="relative h-52 overflow-hidden">
                <img src={a.heroImage} alt={`${a.name}, Bath`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                <div className="absolute bottom-4 left-5">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-[#d9b98a]">{a.city || "Bath"} {a.postcode}</span>
                  <h2 className="font-display text-white text-[26px] font-400 leading-none mt-1">{a.name}</h2>
                </div>
              </div>
              <div className="p-6">
                <p className="text-[14px] text-[#6a6a6a] leading-relaxed">{a.tagline}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-500 text-[#b8935f] group-hover:gap-2.5 transition-all">Explore {a.name} <ArrowRight size={15} /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AreaGuides;
